/**
 * Guía: Clientes.
 *
 * Contenido derivado del capítulo **13** del manual: §0 (qué es un cliente y
 * qué se puede hacer con él), §4 (el listado, la búsqueda y el alcance real
 * de cada tarjeta), §5 (las tres pestañas de la ficha), §6 (los cinco
 * estados y las cuatro reglas del cambio) y §7 (la corrección del vínculo
 * con el creador).
 *
 * Las dos acciones que mueven algo —cambiar el estado de la cuenta y
 * corregir el creador— van sin objetivo: viven dentro de la ficha, y abrirla
 * exige pulsar un cliente. El motor no pulsa por nadie, y un listado vacío
 * no tiene ninguno que pulsar.
 */

import type { TourFlow } from '../types';

const CLIENTES_PATH = '/customers';

export const c13Clientes: TourFlow = {
  id: 'admin.customers',
  chapter: '13',
  title: 'Clientes',
  summary:
    'Quién es la persona, qué compró, y las dos acciones que dejan huella: el estado de su cuenta y su creador vinculado.',
  href: CLIENTES_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'La persona que compra',
      body:
        'Un cliente es el usuario final de la app. Es el único perfil sin rol: no hay ' +
        'permisos que administrar, sólo una persona con su teléfono, su historial y su ' +
        'dinero. Esta pantalla responde tres preguntas —quién es, qué ha comprado y cuánto ' +
        'tiene— y permite dos acciones, las dos auditadas.',
      route: CLIENTES_PATH,
    },
    {
      id: 'country',
      title: 'Sólo los de tu país',
      body:
        'El listado está acotado al país seleccionado en el menú. Si buscas a alguien y no ' +
        'aparece, mira primero qué país tienes puesto: es la causa más común de «este ' +
        'cliente no existe».',
    },
    {
      id: 'stats',
      title: 'Las tres tarjetas no miden lo mismo',
      body:
        'La primera cuenta todos los clientes que cumplen tus filtros. Las otras dos miran ' +
        'sólo la página que tienes delante, y por eso lo dicen en su etiqueta. Sumarlas o ' +
        'compararlas entre sí no significa nada.',
      target: 'admin.customers.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'search',
      title: 'La búsqueda mira cinco columnas',
      body:
        'Nombre, apellido, correo, teléfono y código de referido. Escribe lo que tengas: si ' +
        'un cliente te da su código de referido, sirve para encontrarlo igual que el ' +
        'teléfono. El filtro de al lado acota por estado de la cuenta.',
      target: 'admin.customers.filters',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'status-meaning',
      title: 'El estado dice si puede entrar a la app',
      body:
        'Activa entra y compra. Pendiente de verificación no terminó el alta, y no es una ' +
        'sanción. Suspendida y Bloqueada dejan a la persona fuera de la app y de su ' +
        'billetera: la primera es reversible, la segunda es la definitiva. Es lo primero ' +
        'que hay que mirar cuando alguien dice «no puedo entrar».',
      target: 'admin.customers.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'detail',
      title: 'La ficha, en tres pestañas',
      body:
        'Perfil trae los datos y —lo que más usa soporte— cuándo entró por última vez, ' +
        'desde qué plataforma y cuándo compró por última vez. Órdenes lista sus pedidos. ' +
        'Billetera, sólo para el administrador general, su saldo y sus puntos. Del ' +
        'documento verás el tipo, nunca el número: está cifrado y no sale de la base.',
    },
    {
      id: 'change-status',
      title: 'Suspender saca a la persona en el acto',
      body:
        'Salir de «Activa» revoca todas sus sesiones: no se queda dentro de la app hasta ' +
        'que le caduque el acceso. El motivo es obligatorio y queda en el registro con ' +
        'quién lo hizo y desde qué estado. Reactivar no revoca nada.',
    },
    {
      id: 'block-is-super-admin',
      title: 'Bloquear es del administrador general',
      body:
        'Como responsable de soporte puedes suspender, que es reversible, y también ' +
        'levantar un bloqueo o rebajarlo a suspensión. Lo que no puedes es imponerlo: ' +
        'bloquear es terminal, y por eso queda arriba. No es un permiso que falte, es la ' +
        'decisión de que lo irreversible lo tome una sola figura.',
      roles: ['SUPPORT_LEAD'],
    },
    {
      id: 'creator-link',
      title: 'Corregir el creador mueve dinero futuro',
      body:
        'El cliente captura el código de un creador una sola vez y después no puede ' +
        'cambiarlo. Desde su ficha sí se corrige, y existe para un caso concreto: escribió ' +
        'mal el código. Antes de confirmar verás a quién quedará vinculado. Las comisiones ' +
        'ya generadas no se mueven; lo que cambia es la atribución de aquí en adelante.',
    },
    {
      id: 'addresses',
      title: 'Las direcciones son suyas',
      body:
        'Se ven y no se tocan. Una dirección la edita el cliente desde su app; desde aquí ' +
        'sólo sirven para entender un pedido. Si te pide cambiar una, se le guía para que ' +
        'lo haga él.',
    },
  ],
};
